import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { errMsg, fmtINR, fmtDate } from "@/lib/api";
import { PageHeader, Pager, EmptyState, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const NEXT = {
  requested: ["approved"],
  approved: ["pickup_scheduled"],
  pickup_scheduled: ["in_transit"],
  in_transit: ["received"],
  received: ["inspected"],
  inspected: ["refunded", "closed"],
  refunded: ["closed"],
};

export default function Returns({ scope = "admin" }) {
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [conditionPick, setConditionPick] = useState({});

  const { data } = useQuery({
    queryKey: ["returns", scope, page],
    queryFn: async () => (await api.get(`/returns?page=${page}&limit=15`)).data,
  });

  const advance = useMutation({
    mutationFn: async ({ id, status, condition }) => api.put(`/returns/${id}/status`, { status, condition }),
    onSuccess: (_, v) => {
      toast.success(v.status === "inspected"
        ? (v.condition === "good" ? "Inspected — restocked to inventory" : "Inspected — damaged, NOT restocked")
        : `Return → ${v.status.replace(/_/g, " ")}`);
      qc.invalidateQueries({ queryKey: ["returns"] });
      qc.invalidateQueries({ queryKey: ["orders"] });
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  return (
    <div data-testid={`${scope}-returns-page`}>
      <PageHeader title="Returns" sub="Request → approve → pickup → receive → inspect → restock/refund." testid="returns-title" />

      {!data?.items?.length ? <EmptyState title="No returns" sub="Returns for delivered orders will appear here." /> : (
        <div className="space-y-2">
          {data.items.map((r) => (
            <div key={r.id} data-testid={`return-row-${r.order_no}`} className="rounded-xl border border-slate-800 bg-[#111827] px-4 py-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="text-xs">
                  <div className="flex items-center gap-2">
                    <p className="font-mono text-slate-200">{r.order_no}</p>
                    <StatusBadge status={r.status} />
                    <span className="capitalize text-slate-500">{r.channel?.replace(/_/g, " ")}</span>
                  </div>
                  <p className="mt-1 text-slate-400">{r.customer?.name} · {r.customer?.mobile}</p>
                  <p className="mt-0.5 text-slate-500">{r.reason} · Refund {fmtINR(r.refund_amount)} · {fmtDate(r.created_at)}</p>
                </div>
                <div className="flex items-center gap-2">
                  {(NEXT[r.status] || []).map((ns) =>
                    ns === "inspected" ? (
                      <div key={ns} className="flex items-center gap-1.5">
                        <Select value={conditionPick[r.id] || "good"} onValueChange={(v) => setConditionPick({ ...conditionPick, [r.id]: v })}>
                          <SelectTrigger data-testid={`return-condition-${r.order_no}`} className="h-8 w-32 border-slate-700 bg-slate-900 text-xs text-slate-200">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="border-slate-700 bg-slate-900 text-slate-200">
                            <SelectItem value="good">Good — restock</SelectItem>
                            <SelectItem value="damaged">Damaged</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button data-testid={`return-inspect-${r.order_no}`} size="sm"
                          onClick={() => advance.mutate({ id: r.id, status: "inspected", condition: conditionPick[r.id] || "good" })}
                          className="bg-orange-500 text-white hover:bg-orange-600">Inspect</Button>
                      </div>
                    ) : (
                      <Button key={ns} data-testid={`return-${ns}-${r.order_no}`} size="sm" variant={ns === "refunded" ? "default" : "outline"}
                        onClick={() => advance.mutate({ id: r.id, status: ns })}
                        className={ns === "refunded" ? "bg-emerald-600 text-white hover:bg-emerald-700" : "border-slate-700 capitalize text-slate-300 hover:bg-slate-800"}>
                        {ns.replace(/_/g, " ")}
                      </Button>
                    )
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {data && <Pager page={data.page} pages={data.pages} onPage={setPage} />}
    </div>
  );
}
